import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useShallow } from "zustand/shallow";
import { useUIStore } from "../stores/ui-store";
import { UIState } from "../types/ui-types";
import {
  fetchChat,
  postComment,
  updateComment,
  deleteComment,
} from "../api/comments.api";
import {
  CreateCommentPayload,
  UpdateCommentPayload,
} from "../api/models/comments.models";

interface UseCommentsProps {
  reportId?: number;
}

const useComments = ({ reportId }: UseCommentsProps) => {
  const queryClient = useQueryClient();
  const [bearerToken] = useUIStore(
    useShallow((state: UIState) => [state.bearerToken]),
  );

  // Fetch chat comments
  const {
    data: comments,
    isLoading: isLoadingComments,
    error: commentsError,
  } = useQuery({
    queryKey: ["comments", reportId],
    queryFn: () => fetchChat(bearerToken, reportId),
    enabled: !!reportId && !!bearerToken,
  });

  // Add comment mutation
  const addCommentMutation = useMutation({
    mutationFn: (payload: CreateCommentPayload) => {
      if (!bearerToken) throw new Error("No bearer token available");
      return postComment(bearerToken, payload);
    },
    onSuccess: () => {
      // Invalidate and refetch comments
      queryClient.invalidateQueries({ queryKey: ["comments", reportId] });
    },
    onError: (error) => {
      console.error("Error adding comment:", error);
    },
  });

  // Update comment mutation
  const updateCommentMutation = useMutation({
    mutationFn: (payload: UpdateCommentPayload) => {
      if (!bearerToken) throw new Error("No bearer token available");
      return updateComment(bearerToken, payload);
    },
    onSuccess: () => {
      // Invalidate and refetch comments
      queryClient.invalidateQueries({ queryKey: ["comments", reportId] });
    },
    onError: (error) => {
      console.error("Error updating comment:", error);
    },
  });

  // Delete comment mutation
  const deleteCommentMutation = useMutation({
    mutationFn: (commentId: number) => {
      if (!bearerToken) throw new Error("No bearer token available");
      return deleteComment(bearerToken, commentId);
    },
    onSuccess: () => {
      // Invalidate and refetch comments
      queryClient.invalidateQueries({ queryKey: ["comments", reportId] });
    },
    onError: (error) => {
      console.error("Error deleting comment:", error);
    },
  });

  return {
    // Data
    comments,
    isLoadingComments,
    commentsError,

    // Mutations
    addComment: addCommentMutation.mutate,
    isAddingComment: addCommentMutation.isPending,
    addCommentError: addCommentMutation.error,

    updateComment: updateCommentMutation.mutate,
    isUpdatingComment: updateCommentMutation.isPending,
    updateCommentError: updateCommentMutation.error,

    deleteComment: deleteCommentMutation.mutate,
    isDeletingComment: deleteCommentMutation.isPending,
    deleteCommentError: deleteCommentMutation.error,
  };
};

export default useComments;
